import { FC, useMemo } from 'react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'

import {
  ActionButton,
  Box,
  SelectInput,
  Subheader,
  VStack,
} from '@revolut-internal/ui-kit'

import { CustomPlaceholder as Placeholder } from 'modules/Monitoring/components/Placeholder/Placeholder'
import { Tooltip } from 'modules/Monitoring/components/Tooltip/Tooltip'
import { AlertingActionGetResponse } from 'modules/Monitoring/types'

import { QueueHealthPolicyItem } from '../QueueHealthPolicyItem'
import { AlertingPolicyForm } from '../types'

import { EMPTY_ACTION } from './contst'

type QueueHealthPolicyFormActionsProps = {
  actions?: AlertingActionGetResponse
  isActionsPending: boolean
  isReadonly: boolean
}

const FORM_FIELD_NAME = 'alertingPolicyActions'

export const QueueHealthPolicyFormActions: FC<QueueHealthPolicyFormActionsProps> = ({
  actions,
  isActionsPending,
  isReadonly,
}) => {
  const { control, formState, trigger } = useFormContext<AlertingPolicyForm>()
  const { fields, append, remove } = useFieldArray({
    control,
    name: FORM_FIELD_NAME,
  })

  const isActionsLimitReached = actions?.items?.length === fields.length
  const options = useMemo(() => {
    return (
      actions?.items.map(action => ({
        key: action.alertingActionId,
        value: action.alertingActionId,
        label: action.label,
      })) ?? []
    )
  }, [actions])
  const optionsValues = useMemo(() => options.map(option => option.value), [options])

  const fieldsWithOptions = useMemo(() => {
    return fields.map(field => {
      const fieldOptions = [...options]

      if (field.alertingActionId && !optionsValues.includes(field.alertingActionId)) {
        // add fallback in case action has been deleted
        fieldOptions.push({
          key: field.alertingActionId,
          value: field.alertingActionId,
          label: field.alertingActionId,
        })
      }

      return {
        field,
        options: fieldOptions,
      }
    })
  }, [fields, options, optionsValues])

  const handleDelete = (index: number) => {
    remove(index)

    if (!formState.isSubmitted) {
      return
    }

    trigger()
  }

  return (
    <>
      <Subheader>
        <Subheader.Title>Actions</Subheader.Title>
        {!isReadonly && (
          <Subheader.Side>
            <Tooltip message="Actions limit reached">
              <Box>
                <ActionButton
                  variant="accent"
                  useIcon="Plus"
                  disabled={isActionsLimitReached}
                  onClick={() => append(EMPTY_ACTION)}
                >
                  Add
                </ActionButton>
              </Box>
            </Tooltip>
          </Subheader.Side>
        )}
      </Subheader>

      {!fieldsWithOptions.length && (
        <Placeholder icon="Binoculars" title="No actions added" />
      )}

      <VStack space="s-16">
        {fieldsWithOptions.map(({ field: { id }, options: fieldOptions }, index) => (
          <QueueHealthPolicyItem
            key={id}
            instance="action"
            isReadonly={isReadonly}
            onDelete={() => handleDelete(index)}
          >
            <Controller
              name={`${FORM_FIELD_NAME}.${index}.alertingActionId`}
              control={control}
              rules={{
                required: 'Field is required',
                validate: {
                  unique: (value, formValues) => {
                    const duplicatingFields =
                      formValues[FORM_FIELD_NAME]?.slice(0, index + 1).filter(
                        action => action.alertingActionId === value,
                      ) ?? []

                    return duplicatingFields.length > 1
                      ? 'You can only add an action once'
                      : undefined
                  },
                },
              }}
              render={({ field, fieldState }) => (
                <SelectInput
                  label="Action"
                  labelList="Action"
                  options={fieldOptions}
                  invalid={!!fieldState.error}
                  errorMessage={fieldState.error?.message}
                  disabled={isReadonly}
                  searchable={false}
                  loadingState={isActionsPending ? 'pending' : 'ready'}
                  {...field}
                  onChange={value => {
                    field.onChange(value)

                    if (!formState.isSubmitted) {
                      return
                    }

                    trigger()
                  }}
                />
              )}
            />
          </QueueHealthPolicyItem>
        ))}
      </VStack>
    </>
  )
}
